import { Component, Input, Optional } from '@angular/core';
import { FormControl, FormGroup, AbstractControl } from '@angular/forms';
import { ControlContainer } from '@angular/forms';
import { FileSpecsService } from '../../../core/services/file-specs.service';

@Component({
  selector: 'hbo-field-validation',
  templateUrl: './field-validation.component.html',
  styleUrls: ['./field-validation.component.scss']
})
export class FieldValidationComponent {
  @Input() fieldName!: string;
  @Input() className?: string; // Para mantener compatibilidad con uso anterior

  constructor(
    @Optional() private controlContainer: ControlContainer,
    private fileSpecsService: FileSpecsService
  ) {}

  get control(): AbstractControl | null {
    if (!this.fieldName || !this.controlContainer?.control) return null;
    return this.controlContainer.control.get(this.fieldName);
  }

  get fileSpec() {
    return this.fileSpecsService.getSpec(this.fieldName);
  }

  get shouldShowError(): boolean {
    const ctrl = this.control;
    if (!ctrl) return false;
    
    // Mostrar error solo si está inválido y touched/dirty
    return ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  get validationMessages(): any[] {
    const ctrl = this.control;
    
    if (!ctrl || !this.shouldShowError) return [];

    if (ctrl.errors) {
      const errorKey = Object.keys(ctrl.errors)[0];
      
      // Mapeo de errores estándar a claves de traducción
      const standardErrorMap: { [key: string]: string } = {
        required: 'VALIDATIONS.REQUIRED',
        minlength: 'VALIDATIONS.MINLENGTH',
        maxlength: 'VALIDATIONS.MAXLENGTH',
        min: 'VALIDATIONS.MIN',
        max: 'VALIDATIONS.MAX',
        email: 'VALIDATIONS.INVALID_EMAIL',
        pattern: 'SETTING.MIMESYS.VERTEXDISTANCE',
        rangeError: 'SETTING.MIMESYS.PANTOSCIPICANGLE'
      };

      if (standardErrorMap[errorKey]) {
        return [standardErrorMap[errorKey]];
      }

      // Error de validación de archivo
      if (errorKey === 'fileValidation') {
        const errors = ctrl.errors[errorKey] as string[];
        return this.mapFileErrorsToTranslationKeys(errors);
      }

      // Si es un error personalizado con mensaje string, devolverlo
      const errorValue = ctrl.errors[errorKey];
      if (typeof errorValue === 'string') {
        return [errorValue];
      }
    }

    return [];
  }

  private mapFileErrorsToTranslationKeys(errors: string[]): any[] {
    // Convertir los errores del servicio a objetos con clave y parámetros
    const messages: any[] = [];
    
    errors.forEach(error => {
      if (error.includes('Invalid format')) {
        const formats = this.fileSpec?.formats?.join(', ') || '';
        messages.push({ key: 'SETTING.MIMESYS.FILEEXTERROR', params: { formats } });
        if (this.fieldName === 'screenSaverVideo') {
          messages.push('SETTING.CHANGE_TO_MP4');
        }
      }
      if (error.includes('File too large')) {
        const maxSize = this.fileSpec?.maxSize;
        if (maxSize) {
          const size = `${maxSize.value} ${maxSize.unit}`;
          messages.push({ key: 'SETTING.MAXFILESIZE', params: { size } });
        } else {
          messages.push('SETTING.MAXFILESIZE');
        }
      }
    });

    // Si no hay mapeo específico, devolver los errores originales
    return messages.length > 0 ? messages : errors;
  }
}
